"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import {
  useCookieConsent,
  CONSENT_STORAGE_KEY,
  CATEGORIES_STORAGE_KEY,
} from "@/contexts/CookieConsentContext";
import { useAuth } from "@/contexts/AuthContext";

function Toggle({
  label,
  description,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: (next: boolean) => void;
}) {
  return (
    <label
      className={`flex items-start justify-between gap-4 rounded-lg border px-3 py-2.5 ${
        disabled ? "border-gray-200 bg-gray-50" : "border-gray-200 bg-white"
      }`}
    >
      <span>
        <span className="block text-sm font-medium text-gray-900">
          {label}
          {disabled && (
            <span className="ml-2 rounded bg-gray-200 px-1.5 py-0.5 text-[11px] font-semibold text-gray-600">
              Always on
            </span>
          )}
        </span>
        <span className="mt-0.5 block text-xs text-gray-500">{description}</span>
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={(e) => {
          e.preventDefault();
          onChange?.(!checked);
        }}
        className={`relative mt-1 h-6 w-11 shrink-0 rounded-full transition-colors ${
          checked ? "bg-emerald-600" : "bg-gray-300"
        } ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
      >
        <span
          className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${
            checked ? "left-[22px]" : "left-0.5"
          }`}
        />
      </button>
    </label>
  );
}

/**
 * Site-wide cookie consent banner. Render once inside <Providers> (under both
 * CookieConsentProvider and AuthProvider). Loads on first visit when no stored
 * choice exists; hidden otherwise. Re-openable via the footer "Cookie
 * Settings" entry (fnp:open-cookie-settings window event).
 *
 * Choices: Agree and Proceed (all) · Decline All (none) · Essentials only
 * (necessary-only) · Manage choices (per-category) · View cookie policy (link).
 * The X dismisses without choosing (7-day snooze, not consent).
 */
export function CookieBanner() {
  const {
    isBannerVisible,
    ready,
    categories,
    acceptAll,
    rejectAll,
    savePreferences,
    dismissBanner,
    manageSignal,
    syncSignedInConsent,
  } = useCookieConsent();

  // Mounted under AuthProvider (see app/providers.tsx), so the signed-in id
  // is available for backend user linkage on consent choices.
  const { user } = useAuth();
  const authedUserId = user?.id ?? undefined;

  const [customizing, setCustomizing] = useState(false);
  const [analytics, setAnalytics] = useState(categories.analytics);
  const [marketing, setMarketing] = useState(categories.marketing);

  // Cookie Settings / policy page request the Manage choices view directly.
  useEffect(() => {
    if (manageSignal > 0) setCustomizing(true);
  }, [manageSignal]);

  useEffect(() => {
    setAnalytics(categories.analytics);
    setMarketing(categories.marketing);
  }, [categories.analytics, categories.marketing]);

  // Signed-in source of truth: fetch the persisted backend settings and
  // enforce them locally when a session starts (fresh signups with no record
  // auto opt-in with the user id). Guests never sync — local state only.
  // Runs once per sign-in (not on every callback-identity change): re-running
  // after the user's own choice could fetch the pre-save row and revert it.
  // Live updates from other tabs arrive via the storage listener below.
  const lastSyncedUserRef = useRef<string | null>(null);
  useEffect(() => {
    if (!ready) return;
    if (!authedUserId) {
      lastSyncedUserRef.current = null;
      return;
    }
    if (lastSyncedUserRef.current === authedUserId) return;
    lastSyncedUserRef.current = authedUserId;
    void syncSignedInConsent(authedUserId);
  }, [ready, authedUserId, syncSignedInConsent]);

  // Live cross-tab convergence: every choice writes localStorage, which fires
  // a storage event in other tabs — re-sync from the backend (source of truth)
  // unless the banner editor is open here (never clobber mid-edit).
  useEffect(() => {
    if (!authedUserId) return;
    const onStorage = (e: StorageEvent) => {
      if (
        e.key !== CONSENT_STORAGE_KEY &&
        e.key !== CATEGORIES_STORAGE_KEY
      )
        return;
      if (isBannerVisible) return;
      void syncSignedInConsent(authedUserId);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [authedUserId, isBannerVisible, syncSignedInConsent]);

  if (!ready || !isBannerVisible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      className="fixed inset-x-0 bottom-0 z-[100] px-3 pb-3 sm:px-6 sm:pb-6"
    >
      <div className="relative mx-auto w-full max-w-[1440px] rounded-2xl border border-gray-200 bg-white p-4 shadow-2xl sm:p-6">
        <button
          type="button"
          onClick={dismissBanner}
          aria-label="Dismiss cookie banner (decide later)"
          title="Decide later"
          className="absolute right-3 top-3 rounded-full px-2 py-1 text-lg leading-none text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
        >
          ×
        </button>
        <h2 className="px-8 text-center text-base font-semibold text-gray-900">
          We value your privacy
        </h2>
        <p className="mx-auto mt-1.5 max-w-2xl text-center text-sm leading-relaxed text-gray-600">
          We use cookies to improve your experience on the website — keeping
          you signed in, measuring traffic and, with your permission,
          personalising content.{" "}
          <Link
            href="/cookie-policy"
            className="font-medium text-emerald-700 underline underline-offset-2"
          >
            View cookie policy
          </Link>
          .
        </p>

        {customizing && (
          <div className="mt-4 space-y-2">
            <Toggle
              label="Strictly necessary"
              description="Sign-in, security, load balancing. Required for the site to work."
              checked
              disabled
            />
            <Toggle
              label="Analytics"
              description="Anonymous usage measurement (GA, PostHog, Clarity)."
              checked={analytics}
              onChange={setAnalytics}
            />
            <Toggle
              label="Marketing"
              description="Personalised offers and ad measurement."
              checked={marketing}
              onChange={setMarketing}
            />
          </div>
        )}

        <div className="mt-4 flex flex-col items-stretch gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-center">
          {customizing ? (
            <>
              <button
                type="button"
                onClick={() =>
                  savePreferences({ analytics, marketing }, authedUserId)
                }
                className="rounded-lg bg-emerald-700 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-800"
              >
                Save my choices
              </button>
              <button
                type="button"
                onClick={() => setCustomizing(false)}
                className="rounded-lg px-4 py-2 text-sm font-medium text-gray-500 transition hover:bg-gray-50"
              >
                Close
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => acceptAll(authedUserId)}
                className="rounded-lg bg-emerald-700 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-800"
              >
                Agree
              </button>
              <button
                type="button"
                onClick={() => setCustomizing(true)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
              >
                Manage choices
              </button>
              <button
                type="button"
                onClick={() => rejectAll(authedUserId)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
              >
                Decline
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default CookieBanner;
